import { authFetch, getSessionToken } from "site/services/auth-session.service";
import {
  clearLessonProgressDirty,
  isLessonProgressDirty,
  listDirtyLessonProgressKeys,
  readLessonProgressFromStorage,
  shouldPushLessonProgress,
  type SavedLessonProgress,
  writeLessonProgressToStorage,
} from "site/services/lesson-progress-storage";
import { patchAppSettings, readAppSettings, type AppSettings } from "site/services/settings-storage";
import {
  readLocalLastUpdateSettingsMs,
  writeLocalLastUpdateSettingsMs,
} from "site/services/user-settings-storage";
import {
  readLocalLastUpdateStatsMs,
  writeLocalLastUpdateStatsMs,
} from "site/services/user-stats-storage";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";

type ProgressApiItem = {
  book_id: number;
  text_id: number;
  step_key: string;
  stage_key: string;
  sub_index: number;
  stage_index: number;
  cur_step_item_index: number;
  lesson_completed: boolean;
  progress_percent: number;
};

type StatsTouchResponse = {
  last_update_stats_ms?: number;
};

type SettingsTouchResponse = Partial<AppSettings> & {
  last_update_settings_ms?: number;
};

export type UserDataSyncSource = "authorize" | "refresh";

export type UserDataSyncOpts = {
  sessionSource: UserDataSyncSource;
  serverLastUpdateStatsMs: number;
  serverLastUpdateSettingsMs: number;
};

let syncUserDataInFlight: Promise<void> | null = null;

function canSyncUserData(): boolean {
  return !!(subscriptionApiBaseUrl() && getSessionToken());
}

function authHeaders(jsonBody = false): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${getSessionToken()}`,
  };
  if (jsonBody) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

function normalizeUpdateMs(ms: number | undefined | null): number {
  const value = Number(ms);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function shouldPullProgress(opts: UserDataSyncOpts): boolean {
  if (opts.sessionSource === "authorize") {
    return true;
  }
  return normalizeUpdateMs(opts.serverLastUpdateStatsMs) !== readLocalLastUpdateStatsMs();
}

function shouldPullSettings(opts: UserDataSyncOpts): boolean {
  if (opts.sessionSource === "authorize") {
    return true;
  }
  return normalizeUpdateMs(opts.serverLastUpdateSettingsMs) !== readLocalLastUpdateSettingsMs();
}

function toApiItem(bookId: string, exerciseId: string, data: SavedLessonProgress): ProgressApiItem | null {
  const book_id = Number(bookId);
  const text_id = Number(exerciseId);
  if (!Number.isFinite(book_id) || !Number.isFinite(text_id) || book_id <= 0 || text_id <= 0) {
    return null;
  }
  return {
    book_id,
    text_id,
    step_key: data.stepKey,
    stage_key: data.stageKey,
    sub_index: data.subIndex,
    stage_index: data.stageIndex,
    cur_step_item_index: data.curStepItemIndex,
    lesson_completed: data.lessonCompleted,
    progress_percent: data.progressPercent,
  };
}

function fromApiItem(item: ProgressApiItem): SavedLessonProgress {
  return {
    stepKey: typeof item.step_key === "string" ? item.step_key : "",
    stageKey: typeof item.stage_key === "string" ? item.stage_key : "",
    subIndex: Number(item.sub_index) || 0,
    stageIndex: Number(item.stage_index) || 0,
    curStepItemIndex: Number(item.cur_step_item_index) || 0,
    lessonCompleted: item.lesson_completed === true,
    progressPercent: Math.max(0, Math.min(100, Math.round(Number(item.progress_percent) || 0))),
  };
}

async function applyStatsFromResponse(response: Response): Promise<void> {
  if (!response.ok) {
    return;
  }
  try {
    const data = (await response.json()) as StatsTouchResponse;
    const ms = normalizeUpdateMs(data.last_update_stats_ms);
    if (ms > 0) {
      writeLocalLastUpdateStatsMs(ms);
    }
  } catch {
  }
}

async function applySettingsFromResponse(response: Response): Promise<SettingsTouchResponse | null> {
  if (!response.ok) {
    return null;
  }
  try {
    const data = (await response.json()) as SettingsTouchResponse;
    const ms = normalizeUpdateMs(data.last_update_settings_ms);
    if (ms > 0) {
      writeLocalLastUpdateSettingsMs(ms);
    }
    return data;
  } catch {
    return null;
  }
}

async function pushProgressItems(items: ProgressApiItem[]): Promise<boolean> {
  if (!items.length || !canSyncUserData()) {
    return true;
  }
  const base = subscriptionApiBaseUrl()!;
  const response = await authFetch(`${base.replace(/\/+$/g, "")}/user/progress`, {
    method: "PUT",
    headers: authHeaders(true),
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    return false;
  }
  await applyStatsFromResponse(response);
  return true;
}

export async function pushUserSettingsIfAuthorized(): Promise<void> {
  if (!canSyncUserData()) {
    return;
  }
  const base = subscriptionApiBaseUrl()!;
  const settings = readAppSettings();
  const response = await authFetch(`${base.replace(/\/+$/g, "")}/user/settings`, {
    method: "PUT",
    headers: authHeaders(true),
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    console.log(`user settings push: HTTP ${response.status}`);
    return;
  }
  await applySettingsFromResponse(response);
}

async function pullUserSettingsIfAuthorized(): Promise<void> {
  if (!canSyncUserData()) {
    return;
  }
  const base = subscriptionApiBaseUrl()!;
  const response = await authFetch(`${base.replace(/\/+$/g, "")}/user/settings`, {
    headers: authHeaders(),
  });
  const data = await applySettingsFromResponse(response);
  if (!data) {
    return;
  }
  patchAppSettings({
    autoPlayOnCorrect: data.autoPlayOnCorrect === true,
  });
}

async function pushDirtyLessonProgress(): Promise<void> {
  const keys = listDirtyLessonProgressKeys();
  if (!keys.length || !canSyncUserData()) {
    return;
  }
  const items: ProgressApiItem[] = [];
  const pushedKeys: string[] = [];
  for (const key of keys) {
    const sep = key.indexOf(":");
    if (sep <= 0) {
      continue;
    }
    const bookId = key.slice(0, sep);
    const exerciseId = key.slice(sep + 1);
    if (!isLessonProgressDirty(bookId, exerciseId)) {
      continue;
    }
    const saved = readLessonProgressFromStorage(bookId, exerciseId);
    if (!saved) {
      pushedKeys.push(key);
      continue;
    }
    if (!shouldPushLessonProgress(saved)) {
      continue;
    }
    const item = toApiItem(bookId, exerciseId, saved);
    if (!item) {
      continue;
    }
    items.push(item);
    pushedKeys.push(key);
  }
  if (!items.length) {
    for (const key of pushedKeys) {
      const sep = key.indexOf(":");
      if (sep <= 0) {
        continue;
      }
      clearLessonProgressDirty(key.slice(0, sep), key.slice(sep + 1));
    }
    return;
  }
  const ok = await pushProgressItems(items);
  if (ok) {
    for (const key of pushedKeys) {
      const sep = key.indexOf(":");
      if (sep <= 0) {
        continue;
      }
      clearLessonProgressDirty(key.slice(0, sep), key.slice(sep + 1));
    }
  }
}

async function pullAllLessonProgressFromServer(): Promise<void> {
  if (!canSyncUserData()) {
    return;
  }
  const base = subscriptionApiBaseUrl()!;
  const response = await authFetch(`${base.replace(/\/+$/g, "")}/user/progress`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    return;
  }
  const data = (await response.json()) as { items?: ProgressApiItem[] };
  for (const item of data.items ?? []) {
    const bookId = String(item.book_id);
    const exerciseId = String(item.text_id);
    if (isLessonProgressDirty(bookId, exerciseId)) {
      continue;
    }
    writeLessonProgressToStorage(bookId, exerciseId, fromApiItem(item), { fromServer: true });
  }
}

export async function syncUserDataOnStartup(opts: UserDataSyncOpts): Promise<void> {
  if (!canSyncUserData()) {
    return;
  }
  await pushDirtyLessonProgress();
  if (shouldPullSettings(opts)) {
    await pullUserSettingsIfAuthorized();
  }
  if (shouldPullProgress(opts)) {
    await pullAllLessonProgressFromServer();
  }

  const serverStatsMs = normalizeUpdateMs(opts.serverLastUpdateStatsMs);
  const finalStatsMs = Math.max(serverStatsMs, readLocalLastUpdateStatsMs());
  if (finalStatsMs > 0) {
    writeLocalLastUpdateStatsMs(finalStatsMs);
  }

  const serverSettingsMs = normalizeUpdateMs(opts.serverLastUpdateSettingsMs);
  const finalSettingsMs = Math.max(serverSettingsMs, readLocalLastUpdateSettingsMs());
  if (finalSettingsMs > 0) {
    writeLocalLastUpdateSettingsMs(finalSettingsMs);
  }
}

async function pushSingleLessonProgress(bookId: string, exerciseId: string): Promise<void> {
  if (!isLessonProgressDirty(bookId, exerciseId)) {
    return;
  }
  const saved = readLessonProgressFromStorage(bookId, exerciseId);
  if (!saved) {
    clearLessonProgressDirty(bookId, exerciseId);
    return;
  }
  if (!shouldPushLessonProgress(saved)) {
    return;
  }
  const item = toApiItem(bookId, exerciseId, saved);
  if (!item) {
    return;
  }
  const ok = await pushProgressItems([item]);
  if (ok) {
    clearLessonProgressDirty(bookId, exerciseId);
  }
}

export function scheduleUserDataSync(opts: UserDataSyncOpts): Promise<void> {
  if (!canSyncUserData()) {
    return Promise.resolve();
  }
  if (syncUserDataInFlight) {
    return syncUserDataInFlight;
  }
  syncUserDataInFlight = syncUserDataOnStartup(opts).finally(() => {
    syncUserDataInFlight = null;
  });
  return syncUserDataInFlight;
}

export async function syncLessonProgressOnOpen(bookId: string, exerciseId: string): Promise<void> {
  if (!bookId || !exerciseId || !canSyncUserData()) {
    return;
  }
  await pushSingleLessonProgress(bookId, exerciseId);
}

export async function syncLessonProgressOnClose(bookId: string, exerciseId: string): Promise<void> {
  if (!bookId || !exerciseId || !canSyncUserData()) {
    return;
  }
  await pushSingleLessonProgress(bookId, exerciseId);
}

export async function deleteLessonProgressOnServer(bookId: string, exerciseId: string): Promise<void> {
  if (!canSyncUserData()) {
    return;
  }
  const book_id = Number(bookId);
  const text_id = Number(exerciseId);
  if (!Number.isFinite(book_id) || !Number.isFinite(text_id) || book_id <= 0 || text_id <= 0) {
    return;
  }
  const base = subscriptionApiBaseUrl()!;
  const response = await authFetch(
    `${base.replace(/\/+$/g, "")}/user/progress?book_id=${book_id}&text_id=${text_id}`,
    { method: "DELETE", headers: authHeaders() },
  );
  await applyStatsFromResponse(response);
}
